import { Risk, RiskAssessment, RiskMitigation } from "./types";

export const calculateScore = (assessment: RiskAssessment) => {
  const { impact, people_count, duration, probability, detectability } =
    assessment;
  // Risiko (Rv) = S x N x (F + W + P)
  // Risiko (Rv) = impact x people_count x (duration + probabability + detectability)
  return impact * people_count * (duration + probability + detectability);
};

export const calculateScoreAfterMitigation = (
  risk: Risk,
  includeIdx: number
) => {
  return calculateScore(risk.mitigations[includeIdx!]);
};

export const calculateCompoundRisk = (risks: Risk[]) => {
  const compoundRisk = risks.reduce(
    (acc, risk) =>
      (acc += calculateScore(
        (risk.mitigations?.length &&
          risk.mitigations[risk.mitigations.length - 1]) ||
          risk.assessment
      )),
    0
  );

  return compoundRisk;
};

// Performance level calculation
// Schadensausmaß (S):
// impact < 3 => S1
// impact > 2 => S2

// Aufenthaltsdauer (F):
// duration < 3 => F1
// duration > 2 => F2

// Möglichkeit zum Erkennen und Ausweichen (P):
// detectability < 3 => P1
// detectability > 2 => P2

enum PerformanceLevel {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
}

type PerformanceLevelLookupFn = (
  impactLevel: number,
  durationLevel: number,
  detectabilityLevel: number
) => boolean;

const isTop = (level: number) => level < 3;

const performanceLevelMap: Record<PerformanceLevel, PerformanceLevelLookupFn> =
  {
    // S1 => F1 => P1 => A
    [PerformanceLevel.A]: (iL, dL, detL) =>
      isTop(iL) && isTop(dL) && isTop(detL),

    [PerformanceLevel.B]: (iL, dL, detL) =>
      // S1 => F1 => P2 => B
      (isTop(iL) && isTop(dL)) ||
      // S1 => F2 => P1 => B
      (isTop(iL) && !isTop(dL) && isTop(detL)),

    [PerformanceLevel.C]: (iL, dL, detL) =>
      // S1 => F2 => P2 => C
      (isTop(iL) && !isTop(dL) && !isTop(detL)) ||
      // S2 => F1 => P1 => C
      (!isTop(iL) && isTop(dL) && isTop(detL)),

    [PerformanceLevel.D]: (iL, dL, detL) =>
      // S2 => F1 => P2 => D
      (!isTop(iL) && isTop(dL) && !isTop(detL)) ||
      // S2 => F2 => P1 => D
      (!isTop(iL) && !isTop(dL) && isTop(detL)),

    [PerformanceLevel.E]: (iL, dL, detL) =>
      // S2 => F2 => P2 => E
      !isTop(iL) && !isTop(dL) && !isTop(detL),
  };

export type PlrParams = Pick<
  RiskAssessment,
  "impact" | "duration" | "detectability"
>;

export const calculatePerformanceLevel = (params: PlrParams) => {
  const matchingPerformanceLevel = Object.keys(performanceLevelMap).find(
    (level) => {
      const performanceLevel = level as PerformanceLevel;
      const performanceLevelEvaluator = performanceLevelMap[performanceLevel];

      return performanceLevelEvaluator(
        params.impact,
        params.duration,
        params.detectability
      );
    }
  );

  return matchingPerformanceLevel as PerformanceLevel;
};

// EN ISO 13849-1:2023, Annex A.2: the starting point for determining PLr may be
// taken after independent technical measures have been implemented, rather than
// always from the initial risk. INH (inherently safe design) and EMS
// (supplementary mechanical protection) shift that starting point; ESS and the
// information-for-use measures do not.
const START_POINT_SHIFTING_TYPES: ReadonlySet<string> = new Set(["INH", "EMS"]);

export const resolvePlrStartingPoint = (
  assessment: PlrParams,
  mitigations: RiskMitigation[],
  idx: number
): PlrParams => {
  for (let i = idx - 1; i >= 0; i--) {
    if (START_POINT_SHIFTING_TYPES.has(mitigations[i].type)) {
      return mitigations[i];
    }
  }

  return assessment;
};

// PLr per ESS measure, keyed by the mitigation object itself so the result
// survives a reorder (splicing moves the same references around).
export const performanceLevelsByMitigation = (
  assessment: PlrParams,
  mitigations: RiskMitigation[]
): Map<RiskMitigation, PerformanceLevel> => {
  const levels = new Map<RiskMitigation, PerformanceLevel>();

  mitigations.forEach((mitigation, idx) => {
    if (mitigation.type !== "ESS") {
      return;
    }

    levels.set(
      mitigation,
      calculatePerformanceLevel(
        resolvePlrStartingPoint(assessment, mitigations, idx)
      )
    );
  });

  return levels;
};

export interface PlrChange {
  position: number;
  from: PerformanceLevel;
  to: PerformanceLevel;
}

// Which ESS measures end up requiring a different PLr once the list is
// reordered. `position` is the measure's 1-based position after the move.
export const diffPerformanceLevels = (
  assessment: PlrParams,
  before: RiskMitigation[],
  after: RiskMitigation[]
): PlrChange[] => {
  const levelsBefore = performanceLevelsByMitigation(assessment, before);
  const levelsAfter = performanceLevelsByMitigation(assessment, after);

  const changes: PlrChange[] = [];

  after.forEach((mitigation, idx) => {
    const from = levelsBefore.get(mitigation);
    const to = levelsAfter.get(mitigation);

    if (from && to && from !== to) {
      changes.push({ position: idx + 1, from, to });
    }
  });

  return changes;
};
